/**
 * FUEL IQ DESIGN SYSTEM 2.0 - DESIGN TOKENS
 *
 * A comprehensive design token system for a world-class app experience.
 * Inspired by Apple's Human Interface Guidelines and modern design systems.
 */

export const designTokens = {
  // ===== COLOR PALETTE =====
  colors: {
    // Primary brand colors
    primary: {
      50: '#e5fff3',
      100: '#b3ffe0',
      200: '#80ffcd',
      300: '#4dffba',
      400: '#1affa7',
      500: '#00ff6b', // Main brand color
      600: '#00e05f',
      700: '#00b34c',
      800: '#008639',
      900: '#005926',
    },

    // Secondary accent colors
    secondary: {
      magenta: {
        50: '#ffe5fb',
        100: '#ffb3f5',
        200: '#ff80ef',
        300: '#ff4de9',
        400: '#ff1ae3',
        500: '#ff00e6', // Neon magenta
        600: '#e600cf',
        700: '#b300a1',
        800: '#800073',
        900: '#4d0045',
      },
      blue: {
        50: '#e5f9ff',
        100: '#b3f0ff',
        200: '#80e7ff',
        300: '#4ddeff',
        400: '#1ad5ff',
        500: '#00e0ff', // Neon blue
        600: '#00c7e6',
        700: '#009bb3',
        800: '#006f80',
        900: '#00434d',
      },
      indigo: {
        50: '#eef2ff',
        100: '#e0e7ff',
        200: '#c7d2fe',
        300: '#a5b4fc',
        400: '#818cf8',
        500: '#4f46e5', // Indigo accent
        600: '#4338ca',
        700: '#3730a3',
        800: '#312e81',
        900: '#1e1b4b',
      },
    },

    // Neutral grays with rich depth
    neutral: {
      50: '#f8faff',
      100: '#e6e8f0',
      200: '#d1d5e0',
      300: '#b3b8c5',
      400: '#8e95a8',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#101014',
    },

    // Dark mode backgrounds with subtle gradients
    background: {
      primary: '#101014',
      secondary: '#181824',
      tertiary: '#22232e',
      elevated: '#2a2b38',
      card: '#1a1b26',
    },

    // Semantic colors
    semantic: {
      success: '#00ff6b',
      warning: '#faff00',
      error: '#ff0040',
      info: '#00e0ff',
    },

    // Status indicators
    status: {
      online: '#00ff6b',
      away: '#faff00',
      busy: '#ff0040',
      offline: '#6b7280',
    },
  },

  // ===== TYPOGRAPHY =====
  typography: {
    fontFamily: {
      sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
      mono: "'JetBrains Mono', 'SF Mono', 'Fira Code', 'Consolas', monospace",
      display: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    },

    fontSize: {
      xs: '0.75rem',     // 12px
      sm: '0.875rem',    // 14px
      base: '1rem',      // 16px
      lg: '1.125rem',    // 18px
      xl: '1.25rem',     // 20px
      '2xl': '1.5rem',   // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem',  // 36px
      '5xl': '3rem',     // 48px
      '6xl': '3.75rem',  // 60px
    },

    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },

    lineHeight: {
      none: 1,
      tight: 1.25,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.625,
      loose: 2,
    },

    letterSpacing: {
      tighter: '-0.05em',
      tight: '-0.025em',
      normal: '0',
      wide: '0.025em',
      wider: '0.05em',
      widest: '0.1em',
    },
  },

  // ===== SPACING =====
  spacing: {
    0: '0',
    1: '0.25rem',   // 4px
    2: '0.5rem',    // 8px
    3: '0.75rem',   // 12px
    4: '1rem',      // 16px
    5: '1.25rem',   // 20px
    6: '1.5rem',    // 24px
    8: '2rem',      // 32px
    10: '2.5rem',   // 40px
    12: '3rem',     // 48px
    16: '4rem',     // 64px
    20: '5rem',     // 80px
    24: '6rem',     // 96px
  },

  // ===== BORDER RADIUS =====
  borderRadius: {
    none: '0',
    sm: '0.375rem',   // 6px
    base: '0.5rem',   // 8px
    md: '0.75rem',    // 12px
    lg: '1rem',       // 16px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '2rem',    // 32px
    full: '9999px',
  },

  // ===== SHADOWS =====
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',

    // Enhanced shadows for dark mode
    dark: {
      sm: '0 2px 8px 0 rgba(0, 0, 0, 0.3)',
      md: '0 4px 16px 0 rgba(0, 0, 0, 0.4), 0 2px 8px 0 rgba(0, 0, 0, 0.3)',
      lg: '0 10px 30px 0 rgba(0, 0, 0, 0.5), 0 4px 16px 0 rgba(0, 0, 0, 0.4)',
      xl: '0 20px 50px 0 rgba(0, 0, 0, 0.6), 0 8px 24px 0 rgba(0, 0, 0, 0.5)',
      floating: '0 8px 32px 0 rgba(0, 0, 0, 0.45), 0 3px 16px 0 rgba(0, 0, 0, 0.35)',
      elevated: '0 12px 40px 0 rgba(0, 0, 0, 0.5), 0 4px 20px 0 rgba(0, 0, 0, 0.4)',
    },

    // Glow effects
    glow: {
      green: '0 0 20px 0 rgba(0, 255, 107, 0.3), 0 0 40px 0 rgba(0, 255, 107, 0.15)',
      magenta: '0 0 20px 0 rgba(255, 0, 230, 0.3), 0 0 40px 0 rgba(255, 0, 230, 0.15)',
      blue: '0 0 20px 0 rgba(0, 224, 255, 0.3), 0 0 40px 0 rgba(0, 224, 255, 0.15)',
      subtle: '0 0 20px 0 rgba(0, 255, 107, 0.1)',
    },
  },

  // ===== GRADIENTS =====
  gradients: {
    primary: 'linear-gradient(135deg, #00ff6b 0%, #00e0ff 100%)',
    secondary: 'linear-gradient(135deg, #ff00e6 0%, #4f46e5 100%)',
    success: 'linear-gradient(135deg, #00ff6b 0%, #00ff8c 100%)',
    info: 'linear-gradient(135deg, #00e0ff 0%, #4f46e5 100%)',
    warning: 'linear-gradient(135deg, #faff00 0%, #ff9900 100%)',
    error: 'linear-gradient(135deg, #ff0040 0%, #ff00e6 100%)',

    // Background gradients
    background: {
      primary: 'linear-gradient(180deg, #1b1c25, #14151d)',
      secondary: 'linear-gradient(180deg, #22232e, #1a1b26)',
      tertiary: 'linear-gradient(180deg, #2a2b38, #22232e)',
      card: 'var(--bg-card)',
      elevated: 'var(--bg-card)',
    },

    // Mesh gradients for premium feel
    mesh: {
      primary: 'radial-gradient(at 0% 0%, #00ff6b 0%, transparent 50%), radial-gradient(at 100% 0%, #00e0ff 0%, transparent 50%), radial-gradient(at 100% 100%, #ff00e6 0%, transparent 50%), radial-gradient(at 0% 100%, #4f46e5 0%, transparent 50%)',
      subtle: 'radial-gradient(circle at 20% 80%, rgba(0,255,107,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(0,224,255,0.08) 0%, transparent 50%)',
    },

    // Glass effects
    glass: {
      light: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 100%)',
      medium: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)',
      strong: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)',
    },
  },

  // ===== TRANSITIONS =====
  transitions: {
    duration: {
      instant: '50ms',
      fast: '150ms',
      base: '250ms',
      slow: '400ms',
      slower: '600ms',
    },

    timing: {
      linear: 'linear',
      ease: 'ease',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)', // Bouncy spring effect
      smooth: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    },
  },

  // ===== Z-INDEX SCALE =====
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    backdrop: 1040,
    offcanvas: 1050,
    modal: 1060,
    popover: 1070,
    tooltip: 1080,
    notification: 1090,
  },

  // ===== BREAKPOINTS =====
  breakpoints: {
    xs: '0px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },

  // ===== BLUR EFFECTS =====
  blur: {
    none: '0',
    sm: 'blur(4px)',
    base: 'blur(8px)',
    md: 'blur(12px)',
    lg: 'blur(16px)',
    xl: 'blur(24px)',
    '2xl': 'blur(40px)',
    '3xl': 'blur(64px)',
  },
};

export type DesignTokens = typeof designTokens;
